import React, { useEffect, useState } from 'react';
import Layout from '../components/Layout';
import { getMeetings, createMeeting, updateMeeting, deleteMeeting, getProjects, getTasks } from '../api';
import UserSelector from '../components/UserSelector';

const DAYS    = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS  = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const DURATIONS = [15, 30, 45, 60, 90, 120];

const emptyForm = {
  title: '', description: '', date: '', time: '09:00',
  duration: 60, location: '', project_id: '', invitees: [],
};

export default function CalendarPage() {
  const currentUser = JSON.parse(localStorage.getItem('gpf_user') || '{}');
  const today       = new Date();

  const [meetings,   setMeetings]   = useState([]);
  const [projects,   setProjects]   = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [projectTasks, setProjectTasks] = useState([]);
  const [viewDate,   setViewDate]   = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  const [showForm,   setShowForm]   = useState(false);
  const [form,       setForm]       = useState(emptyForm);
  const [saving,     setSaving]     = useState(false);
  const [selected,   setSelected]   = useState(null);  // selected meeting for detail view
  const [clickedDay, setClickedDay] = useState(null);  // day clicked to pre-fill form
  const [editingMeeting, setEditingMeeting] = useState(null); // meeting being edited

  useEffect(() => {
    Promise.allSettled([getMeetings(), getProjects()]).then(([mRes, pRes]) => {
      if (mRes.status === 'fulfilled') setMeetings(mRes.value.data);
      else console.error('Meetings fetch failed:', mRes.reason);
      if (pRes.status === 'fulfilled') setProjects(pRes.value.data);
    }).finally(() => setLoading(false));
  }, []);

  // ── Calendar helpers ──
  const year  = viewDate.getFullYear();
  const month = viewDate.getMonth();
  const firstDay    = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const todayStr    = today.toISOString().split('T')[0];

  const prevMonth = () => setViewDate(new Date(year, month - 1, 1));
  const nextMonth = () => setViewDate(new Date(year, month + 1, 1));

  const getMeetingsForDay = (day) => {
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return meetings.filter(m => m.meta?.gpf_date === dateStr);
  };

  // Load tasks when project selected in form
  useEffect(() => {
    if (form.project_id) {
      getTasks(form.project_id).then(res => setProjectTasks(res.data)).catch(console.error);
    } else {
      setProjectTasks([]);
    }
  }, [form.project_id]);

  const handleDayClick = (day) => {
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    setForm({ ...emptyForm, date: dateStr });
    setClickedDay(dateStr);
    setShowForm(true);
    setSelected(null);
  };

  const handleEdit = (meeting) => {
    setEditingMeeting(meeting);
    setForm({
      title:       meeting.title?.rendered || '',
      description: meeting.content?.rendered?.replace(/<[^>]+>/g, '') || '',
      date:        meeting.meta?.gpf_date      || '',
      time:        meeting.meta?.gpf_time      || '09:00',
      duration:    meeting.meta?.gpf_duration  || 60,
      location:    meeting.meta?.gpf_location  || '',
      project_id:  meeting.meta?.gpf_project_id?.toString() || '',
      task_id:     meeting.meta?.gpf_task_id?.toString()    || '',
      invitees:    JSON.parse(meeting.meta?.gpf_invitees || '[]'),
    });
    setShowForm(true);
    setSelected(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.project_id) {
      alert('Please link this meeting to a project.');
      return;
    }
    setSaving(true);
    try {
      const uid = parseInt(currentUser.id) || 0;
      if (editingMeeting) {
        await updateMeeting(editingMeeting.id, { ...form, organizer_id: uid });
      } else {
        await createMeeting({ ...form, organizer_id: uid });
      }
      getMeetings().then(res => setMeetings(res.data)).catch(console.error);
      setShowForm(false);
      setForm(emptyForm);
      setEditingMeeting(null);
    } catch (err) {
      console.error('Create meeting error:', err);
      alert('Failed to create meeting: ' + (err.response?.data?.message || err.message));
    }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this meeting?')) return;
    await deleteMeeting(id);
    setMeetings(meetings.filter(m => m.id !== id));
    setSelected(null);
  };

  const formatTime = (time) => {
    if (!time) return '';
    const [h, m] = time.split(':');
    const hour = parseInt(h);
    return `${hour > 12 ? hour - 12 : hour || 12}:${m} ${hour >= 12 ? 'PM' : 'AM'}`;
  };

  const meetingColor = (i) => ['#2563eb', '#7c3aed', '#0891b2', '#16a34a', '#d97706', '#dc2626'][i % 6];

  // Upcoming meetings (next 7 days)
  const upcoming = meetings
    .filter(m => m.meta?.gpf_date >= todayStr)
    .sort((a, b) => (a.meta?.gpf_date + a.meta?.gpf_time).localeCompare(b.meta?.gpf_date + b.meta?.gpf_time))
    .slice(0, 5);

  return (
    <Layout>
      <div style={styles.page}>
        <div style={styles.header}>
          <div>
            <h1 style={styles.title}>Calendar</h1>
            <p style={styles.subtitle}>Schedule and manage meetings</p>
          </div>
          <button style={styles.primaryBtn} onClick={() => { setForm({ ...emptyForm, date: todayStr }); setShowForm(true); setSelected(null); }}>
            + Schedule Meeting
          </button>
        </div>

        <div style={styles.layout}>

          {/* ── Left: Calendar ── */}
          <div style={styles.calendarWrap}>
            <div style={styles.calNav}>
              <button style={styles.navBtn} onClick={prevMonth}>‹</button>
              <h2 style={styles.monthTitle}>{MONTHS[month]} {year}</h2>
              <button style={styles.navBtn} onClick={nextMonth}>›</button>
            </div>

            <div style={styles.calGrid}>
              {DAYS.map(d => <div key={d} style={styles.dayHeader}>{d}</div>)}
              {Array.from({ length: firstDay }).map((_, i) => <div key={`empty-${i}`} />)}
              {Array.from({ length: daysInMonth }).map((_, i) => {
                const day       = i + 1;
                const dateStr   = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                const dayMeetings = getMeetingsForDay(day);
                const isToday   = dateStr === todayStr;
                const isPast    = dateStr < todayStr;
                return (
                  <div
                    key={day}
                    style={{ ...styles.dayCell, background: isToday ? '#eff6ff' : '#fff', opacity: isPast ? 0.7 : 1, cursor: 'pointer' }}
                    onClick={() => handleDayClick(day)}
                  >
                    <span style={{ ...styles.dayNum, background: isToday ? '#2563eb' : 'transparent', color: isToday ? '#fff' : '#0f172a' }}>
                      {day}
                    </span>
                    <div style={styles.meetingDots}>
                      {dayMeetings.slice(0, 3).map((m, idx) => (
                        <div
                          key={m.id}
                          style={{ ...styles.meetingChip, background: meetingColor(idx) }}
                          onClick={e => { e.stopPropagation(); setSelected(m); setShowForm(false); }}
                          title={`${m.title?.rendered} — ${projects.find(p => p.id === m.meta?.gpf_project_id)?.title?.rendered || 'No project'}`}
                        >
                          {m.title?.rendered?.slice(0, 12)}{m.title?.rendered?.length > 12 ? '…' : ''}
                        </div>
                      ))}
                      {dayMeetings.length > 3 && <span style={styles.moreMeetings}>+{dayMeetings.length - 3} more</span>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* ── Right: Panel ── */}
          <div style={styles.rightPanel}>

            {/* Meeting detail */}
            {selected && !showForm && (
              <div style={styles.detailCard}>
                <div style={styles.detailHeader}>
                  <h3 style={styles.detailTitle}>{selected.title?.rendered}</h3>
                  <button style={styles.closeBtn} onClick={() => setSelected(null)}>✕</button>
                </div>
                <div style={styles.detailRows}>
                  {selected.meta?.gpf_project_id > 0 && (
                    <div style={{ ...styles.detailRow, marginBottom: 8 }}>
                      <span style={styles.detailLabel}>📁 Project</span>
                      <span style={{ fontWeight: 600, color: '#2563eb' }}>
                        {projects.find(p => p.id === selected.meta?.gpf_project_id)?.title?.rendered || 'Unknown'}
                      </span>
                    </div>
                  )}
                  <div style={{ ...styles.detailRow, padding: '6px 0', borderBottom: '1px solid #f1f5f9', marginBottom: 4 }}><span style={{ fontSize: 12, color: '#64748b' }}>Scheduled by</span><span style={{ fontSize: 13, fontWeight: 700, color: '#2563eb' }}>{selected.gpf_organizer_name}</span></div>
                  <div style={styles.detailRow}><span style={styles.detailLabel}>📅 Date</span><span>{selected.meta?.gpf_date}</span></div>
                  <div style={styles.detailRow}><span style={styles.detailLabel}>🕐 Time</span><span>{formatTime(selected.meta?.gpf_time)}</span></div>
                  <div style={styles.detailRow}><span style={styles.detailLabel}>⏱ Duration</span><span>{selected.meta?.gpf_duration} min</span></div>
                  {selected.meta?.gpf_location && <div style={styles.detailRow}><span style={styles.detailLabel}>📍 Location</span><span>{selected.meta.gpf_location}</span></div>}
                  {selected.gpf_invitee_names?.length > 0 && (
                    <div style={styles.detailRow}>
                      <span style={styles.detailLabel}>👥 Invitees</span>
                      <span>{selected.gpf_invitee_names.map(u => u.name).join(', ')}</span>
                    </div>
                  )}
                  {selected.content?.rendered && (
                    <div style={{ ...styles.detailRow, flexDirection: 'column', gap: 4 }}>
                      <span style={styles.detailLabel}>📝 Notes</span>
                      <p style={{ margin: 0, fontSize: 13, color: '#374151' }} dangerouslySetInnerHTML={{ __html: selected.content.rendered }} />
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                  <button style={styles.editBtn2} onClick={() => handleEdit(selected)}>✎ Edit</button>
                  <button style={styles.deleteBtn2} onClick={() => handleDelete(selected.id)}>Delete</button>
                </div>
              </div>
            )}

            {/* Create meeting form */}
            {showForm && (
              <div style={styles.formCard}>
                <div style={styles.formHeader}>
                  <h3 style={styles.formTitle}>{editingMeeting ? 'Edit Meeting' : 'Schedule Meeting'}</h3>
                  <button style={styles.closeBtn} onClick={() => { setShowForm(false); setEditingMeeting(null); setForm(emptyForm); }}>✕</button>
                </div>
                <form onSubmit={handleSubmit} style={styles.form}>
                  <div style={styles.field}>
                    <label style={styles.label}>Title *</label>
                    <input style={styles.input} value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="Meeting title" required />
                  </div>
                  <div style={styles.row2}>
                    <div style={styles.field}>
                      <label style={styles.label}>Date *</label>
                      <input style={styles.input} type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} required />
                    </div>
                    <div style={styles.field}>
                      <label style={styles.label}>Time *</label>
                      <input style={styles.input} type="time" value={form.time} onChange={e => setForm({ ...form, time: e.target.value })} required />
                    </div>
                  </div>
                  <div style={styles.row2}>
                    <div style={styles.field}>
                      <label style={styles.label}>Duration</label>
                      <select style={styles.input} value={form.duration} onChange={e => setForm({ ...form, duration: parseInt(e.target.value) })}>
                        {DURATIONS.map(d => <option key={d} value={d}>{d < 60 ? `${d} min` : `${d / 60}h`}</option>)}
                      </select>
                    </div>
                    <div style={styles.field}>
                      <label style={styles.label}>Project * <span style={{ color: '#dc2626' }}>Required</span></label>
                      <select style={{ ...styles.input, borderColor: !form.project_id ? '#fca5a5' : '#d1d5db' }} value={form.project_id} onChange={e => setForm({ ...form, project_id: e.target.value, task_id: '' })} required>
                        <option value="">Select project...</option>
                        {projects.map(p => <option key={p.id} value={p.id}>{p.title?.rendered}</option>)}
                      </select>
                    </div>
                  </div>
                  {form.project_id && (
                    <div style={styles.field}>
                      <label style={styles.label}>Link to Task (optional)</label>
                      <select style={styles.input} value={form.task_id || ''} onChange={e => setForm({ ...form, task_id: e.target.value })}>
                        <option value="">Select task...</option>
                        {projectTasks.map(t => <option key={t.id} value={t.id}>{t.title?.rendered}</option>)}
                      </select>
                    </div>
                  )}
                  <div style={styles.field}>
                    <label style={styles.label}>Location</label>
                    <input style={styles.input} value={form.location} onChange={e => setForm({ ...form, location: e.target.value })} placeholder="e.g. Zoom, Conference Room A" />
                  </div>
                  <div style={styles.field}>
                    <label style={styles.label}>Description</label>
                    <textarea style={{ ...styles.input, height: 60, resize: 'vertical' }} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Agenda or notes..." />
                  </div>
                  <div style={styles.field}>
                    <label style={styles.label}>Invite Users</label>
                    <UserSelector selected={form.invitees} onChange={val => setForm({ ...form, invitees: val })} />
                  </div>
                  <button type="submit" style={styles.primaryBtn} disabled={saving}>
                    {saving ? 'Saving...' : editingMeeting ? 'Save Changes' : 'Schedule Meeting'}
                  </button>
                </form>
              </div>
            )}

            {/* Upcoming meetings */}
            {!showForm && !selected && (
              <div style={styles.upcomingCard}>
                <h3 style={styles.upcomingTitle}>Upcoming Meetings</h3>
                {upcoming.length === 0 ? (
                  <p style={styles.noUpcoming}>No upcoming meetings.</p>
                ) : (
                  <div style={styles.upcomingList}>
                    {upcoming.map((m, i) => (
                      <div key={m.id} style={styles.upcomingItem} onClick={() => setSelected(m)}>
                        <div style={{ ...styles.upcomingColor, background: meetingColor(i) }} />
                        <div style={styles.upcomingInfo}>
                          <span style={styles.upcomingName}>{m.title?.rendered}</span>
                          <span style={styles.upcomingMeta}>{m.meta?.gpf_date} · {formatTime(m.meta?.gpf_time)} · {m.meta?.gpf_duration}min</span>
                          {m.meta?.gpf_project_id > 0 && (
                            <span style={{ fontSize: 10, fontWeight: 700, color: '#2563eb', background: '#eff6ff', padding: '1px 6px', borderRadius: 99, display: 'inline-block', marginTop: 2 }}>
                              {projects.find(p => p.id === m.meta?.gpf_project_id)?.title?.rendered?.toUpperCase() || ''}
                            </span>
                          )}
                          <span style={{ ...styles.upcomingMeta, color: '#64748b' }}>by {m.gpf_organizer_name || 'Unknown'}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}

const styles = {
  page:          { maxWidth: 1100 },
  header:        { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 },
  title:         { fontSize: 24, fontWeight: 700, color: '#0f172a', margin: 0 },
  subtitle:      { color: '#64748b', fontSize: 14, margin: '4px 0 0' },
  primaryBtn:    { padding: '10px 20px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer', width: '100%' },
  layout:        { display: 'flex', gap: 20, alignItems: 'flex-start', flexWrap: 'wrap' },
  calendarWrap:  { flex: 1, background: '#fff', borderRadius: 10, padding: 16, boxShadow: '0 1px 4px rgba(0,0,0,0.06)', minWidth: 280 },
  calNav:        { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 },
  navBtn:        { background: 'none', border: '1px solid #e2e8f0', borderRadius: 6, width: 32, height: 32, cursor: 'pointer', fontSize: 18, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  monthTitle:    { fontSize: 16, fontWeight: 700, color: '#0f172a', margin: 0 },
  calGrid:       { display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2 },
  dayHeader:     { fontSize: 11, fontWeight: 700, color: '#94a3b8', textAlign: 'center', padding: '6px 0', textTransform: 'uppercase' },
  dayCell:       { minHeight: 60, borderRadius: 4, padding: 4, border: '1px solid #f1f5f9', display: 'flex', flexDirection: 'column', gap: 2 },
  dayNum:        { width: 24, height: 24, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 600, flexShrink: 0 },
  meetingDots:   { display: 'flex', flexDirection: 'column', gap: 2, flex: 1 },
  meetingChip:   { fontSize: 10, color: '#fff', padding: '2px 4px', borderRadius: 3, cursor: 'pointer', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  moreMeetings:  { fontSize: 10, color: '#94a3b8' },
  rightPanel:    { width: 300, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 16, minWidth: 280, maxWidth: '100%' },
  detailCard:    { background: '#fff', borderRadius: 10, padding: 20, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' },
  detailHeader:  { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 },
  detailTitle:   { fontSize: 15, fontWeight: 700, color: '#0f172a', margin: 0, flex: 1, marginRight: 8 },
  closeBtn:      { background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: 16 },
  detailRows:    { display: 'flex', flexDirection: 'column', gap: 10 },
  detailRow:     { display: 'flex', gap: 8, fontSize: 13 },
  detailLabel:   { color: '#64748b', minWidth: 90, flexShrink: 0 },
  editBtn2:      { flex: 1, padding: '8px', background: '#eff6ff', color: '#2563eb', border: '1px solid #bfdbfe', borderRadius: 7, fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  deleteBtn2:    { flex: 1, padding: '8px', background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca', borderRadius: 7, fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  formCard:      { background: '#fff', borderRadius: 10, padding: 20, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' },
  formHeader:    { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 },
  formTitle:     { fontSize: 15, fontWeight: 700, color: '#0f172a', margin: 0 },
  form:          { display: 'flex', flexDirection: 'column', gap: 12 },
  row2:          { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 },
  field:         { display: 'flex', flexDirection: 'column', gap: 5 },
  label:         { fontSize: 12, fontWeight: 600, color: '#374151' },
  input:         { padding: '8px 10px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 13, outline: 'none', fontFamily: 'inherit', width: '100%', boxSizing: 'border-box' },
  upcomingCard:  { background: '#fff', borderRadius: 10, padding: 20, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' },
  upcomingTitle: { fontSize: 14, fontWeight: 700, color: '#0f172a', margin: '0 0 14px' },
  noUpcoming:    { color: '#94a3b8', fontSize: 13 },
  upcomingList:  { display: 'flex', flexDirection: 'column', gap: 8 },
  upcomingItem:  { display: 'flex', gap: 10, alignItems: 'flex-start', cursor: 'pointer', padding: '8px', borderRadius: 6, background: '#f8fafc' },
  upcomingColor: { width: 4, height: '100%', minHeight: 36, borderRadius: 99, flexShrink: 0 },
  upcomingInfo:  { flex: 1, minWidth: 0 },
  upcomingName:  { fontSize: 13, fontWeight: 600, color: '#0f172a', display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  upcomingMeta:  { fontSize: 11, color: '#94a3b8', display: 'block', marginTop: 2 },
};